import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { User, authService } from '../services/authService';
import { readStoredTextSize, TextSize } from '../utils/textSize';

// Admin app state interface
interface AppState {
  user: User | null;
  isLoading: boolean;
  theme: 'light' | 'dark';
  textSize: TextSize;
  adminStats: {
    totalUsers: number;
    totalPosts: number;
    totalComments: number;
    activeUsers: number;
  } | null;
  isAdmin: boolean;
  adminAccessChecked: boolean;
  userRole: 'PATIENT' | 'CAREGIVER' | 'ADMIN' | null;
}

// Initial state
const initialState: AppState = {
  user: null,
  isLoading: false,
  theme: 'light',
  textSize: readStoredTextSize(),
  adminStats: null,
  isAdmin: false,
  adminAccessChecked: false,
  userRole: null,
};

// Action types
type AppAction =
  | { type: 'SET_USER'; payload: AppState['user'] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_THEME'; payload: 'light' | 'dark' }
  | { type: 'SET_TEXT_SIZE'; payload: TextSize }
  | { type: 'SET_ADMIN_STATS'; payload: AppState['adminStats'] }
  | { type: 'SET_ADMIN_ACCESS'; payload: { isAdmin: boolean; checked: boolean } }
  | { type: 'LOGOUT' };

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_USER':
      return { 
        ...state, 
        user: action.payload,
        isAdmin: action.payload ? authService.isAdmin(action.payload) : false,
        userRole: action.payload?.role || null
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_THEME':
      return { ...state, theme: action.payload };
    case 'SET_TEXT_SIZE':
      return { ...state, textSize: action.payload };
    case 'SET_ADMIN_STATS':
      return { ...state, adminStats: action.payload };
    case 'SET_ADMIN_ACCESS':
      return { 
        ...state, 
        isAdmin: action.payload.isAdmin,
        adminAccessChecked: action.payload.checked
      };
    case 'LOGOUT':
      return { 
        ...state, 
        user: null, 
        adminStats: null,
        isAdmin: false,
        adminAccessChecked: false,
        userRole: null
      };
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Check admin access on app initialization
  useEffect(() => {
    const checkAdminAccess = async () => {
      if (authService.isAuthenticated() && !state.adminAccessChecked) {
        dispatch({ type: 'SET_LOADING', payload: true });
        
        try {
          const hasAdminAccess = await authService.validateAdminAccess();
          dispatch({ 
            type: 'SET_ADMIN_ACCESS', 
            payload: { isAdmin: hasAdminAccess, checked: true }
          });
          
          if (hasAdminAccess) {
            // Load user profile if admin access is confirmed
            const profile = await authService.getProfile();
            dispatch({ type: 'SET_USER', payload: profile.data });
          }
        } catch (error) {
          console.error('Admin access check failed:', error);
          dispatch({ 
            type: 'SET_ADMIN_ACCESS', 
            payload: { isAdmin: false, checked: true }
          });
          // Clear invalid token
          authService.removeToken();
        } finally {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else if (!authService.isAuthenticated()) {
        dispatch({ 
          type: 'SET_ADMIN_ACCESS', 
          payload: { isAdmin: false, checked: true }
        });
      }
    };

    checkAdminAccess();
  }, [state.adminAccessChecked]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
};

// Hook to use the context
export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};
